import { PaymentResponsibility, SubscriptionPlan, SubscriptionsByPaymentRole } from "@app/models";
import { TranslocoService } from "@ngneat/transloco";
import { ItemWithLabel } from "clearline-common";

export const POS_MARKETING_PLAN_FAMILY = "PosMarketing";

export interface SubscriptionPlanOption extends ItemWithLabel<number> {
  /** Translated group label used by the dropdown `groupBy`. */
  family: string;
  /** True for additional partner plans (Clover POS Marketing) shown apart from the standard ones. */
  isAdditional: boolean;
}

export function groupSubscriptionsByPaymentRole(
  accountOwnerSubs: SubscriptionPlan[],
  partnerOwnerSubs: SubscriptionPlan[],
  transloco: TranslocoService
): SubscriptionsByPaymentRole {
  const accountOwnerList: ItemWithLabel<number>[] = accountOwnerSubs.map((subscription) => ({
    label: buildSubscriptionLabel(subscription, transloco),
    value: subscription.id
  }));

  const partnerList: ItemWithLabel<number>[] = partnerOwnerSubs.map((subscription) => ({
    label: buildSubscriptionLabel(subscription, transloco),
    value: subscription.id
  }));

  return {
    [PaymentResponsibility.AccountOwner]: accountOwnerList,
    [PaymentResponsibility.Partner]: partnerList
  };
}

/**
 * Maps subscription plans to dropdown options, tagging the additional Clover POS Marketing
 * plans so the selector can group them apart from the standard plans.
 */
export function mapSubscriptionPlanOptions(plans: SubscriptionPlan[], transloco: TranslocoService): SubscriptionPlanOption[] {
  const standardGroup = transloco.translate("newRegistrations.planFamily.standard");

  return plans.map((plan) => {
    const isAdditional = plan.planFamily === POS_MARKETING_PLAN_FAMILY;
    const familyKey = `newRegistrations.planFamily.${plan.planFamily}`;
    const familyLabel = isAdditional ? translateWithFallback(transloco, familyKey, plan.planFamily) : standardGroup;

    return {
      label: buildSubscriptionLabel(plan, transloco),
      value: plan.id,
      family: familyLabel,
      isAdditional
    };
  });
}

function translateWithFallback(transloco: TranslocoService, key: string, fallback: string): string {
  const translated = transloco.translate(key);

  return translated === key ? fallback : translated;
}

export function buildSubscriptionLabel(subscription: SubscriptionPlan, transloco: TranslocoService): string {
  const i18nUnlimited = transloco.translate("newRegistrations.unlimited");
  const { name, limits, description } = subscription;
  const periodLimit = limits.isUnlimited ? i18nUnlimited : limits.periodLimit;

  return `${name} ${periodLimit || ""} (${description})`;
}
